/**
 * Annual Excursion Letter Tab Component
 * Annual notification letter to local authorities about childcare center operations and excursion routes
 * Features: Formal letter format, fully customizable, save to database, email, print-friendly
 */
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Truck, Printer, Plus, Trash2, Save, History, ChevronLeft, CheckSquare, Square, Mail, MapPin, Building2, RefreshCw, FileText, Eye, EyeOff, Download, Upload } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { exportTransportationPDF } from "@/utils/pdfExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TransportationNotificationTab = ({ centerName = "", organization = {}, onComplianceChange }) => {
  const [view, setView] = useState("form"); // "form" or "history"
  const [showForm, setShowForm] = useState(true); // Toggle form inputs visibility
  const [savedNotifications, setSavedNotifications] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const letterRef = useRef(null);

  // Get organization name for default
  const orgName = organization?.name || centerName || "";

  // Letter date
  const [letterDate, setLetterDate] = useState(format(new Date(), 'MMMM d, yyyy'));
  
  // Center Information - initialized with organization name
  const [centerInfo, setCenterInfo] = useState({
    name: orgName,
    address: "",
    capacity: "",
    primaryContact: "",
    primaryPhone: "",
    primaryCell: "",
    secondaryContact: "",
    secondaryCell: ""
  });

  // Designated Pick-Up and Drop-Off Locations
  const [pickupDropoff, setPickupDropoff] = useState({
    primaryDropOff: "",
    primaryPickUp: "",
    secondaryLocation: ""
  });

  // Excursion Locations
  const [excursionLocations, setExcursionLocations] = useState([
    { id: 1, name: "", address: "" }
  ]);

  // CC Recipients (local authorities)
  const [ccRecipients, setCcRecipients] = useState([
    { id: 1, name: "", address: "" }
  ]);

  // Additional notes/attachments mention
  const [attachmentNote, setAttachmentNote] = useState("");

  // Signature
  const [signature, setSignature] = useState({
    name: "",
    title: ""
  });

  // Email for sending
  const [recipientEmail, setRecipientEmail] = useState("");

  // Update center name when organization loads (for async loading)
  useEffect(() => {
    if (orgName && !centerInfo.name) {
      setCenterInfo(prev => ({ ...prev, name: orgName }));
    }
  }, [orgName]);

  // Fetch saved notifications
  const fetchSavedNotifications = async () => {
    setLoadingNotifications(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/transportation-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedNotifications(response.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchSavedNotifications();
  }, []);

  // Refresh when switching to history
  useEffect(() => {
    if (view === "history") {
      fetchSavedNotifications();
    }
  }, [view]);

  // Pre-fill from last notification
  const handlePrefill = () => {
    if (savedNotifications.length === 0) {
      toast.error("No saved notifications to pre-fill from");
      return;
    }

    const lastNotification = savedNotifications[0];
    
    // Update letter date to current date
    setLetterDate(format(new Date(), 'MMMM d, yyyy'));
    
    // Pre-fill center info
    if (lastNotification.centerInfo) {
      setCenterInfo(lastNotification.centerInfo);
    }
    
    // Pre-fill excursion locations
    if (lastNotification.excursionLocations?.length > 0) {
      setExcursionLocations(lastNotification.excursionLocations.map((l, i) => ({
        ...l,
        id: l.id || i + 1
      })));
    }
    
    // Pre-fill CC recipients
    if (lastNotification.ccRecipients?.length > 0) {
      setCcRecipients(lastNotification.ccRecipients.map((r, i) => ({
        ...r,
        id: r.id || i + 1
      })));
    }
    
    // Pre-fill attachment note
    if (lastNotification.attachmentNote) {
      setAttachmentNote(lastNotification.attachmentNote);
    }
    
    // Pre-fill pickup/dropoff locations
    if (lastNotification.pickupDropoff) {
      setPickupDropoff(lastNotification.pickupDropoff);
    }
    
    // Pre-fill signature
    if (lastNotification.signature) {
      setSignature(lastNotification.signature);
    }
    
    toast.success("Form pre-filled from last notification! Date updated to today.");
  };

  // Save notification
  const handleSave = async () => {
    if (!centerInfo.name || !centerInfo.address) {
      toast.error("Please fill in at least center name and address");
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const notificationData = {
        letterDate,
        centerInfo,
        pickupDropoff,
        excursionLocations: excursionLocations.filter(l => l.name || l.address),
        ccRecipients: ccRecipients.filter(r => r.name || r.address),
        attachmentNote,
        signature
      };

      await axios.post(`${API}/transportation-notifications`, notificationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Notification saved successfully!");
      fetchSavedNotifications();
      if (onComplianceChange) onComplianceChange();
    } catch (error) {
      console.error("Error saving notification:", error);
      toast.error("Failed to save notification");
    } finally {
      setSaving(false);
    }
  };

  // Delete single notification
  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/transportation-notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Notification deleted");
      fetchSavedNotifications();
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedNotifications.length === 0) {
      toast.error("No notifications selected");
      return;
    }
    
    if (!window.confirm(`Delete ${selectedNotifications.length} selected notifications?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/transportation-notifications/bulk-delete`, 
        { ids: selectedNotifications },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Deleted ${selectedNotifications.length} notifications`);
      setSelectedNotifications([]);
      fetchSavedNotifications();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Failed to delete notifications");
    }
  };

  // Toggle selection
  const toggleNotificationSelection = (notificationId) => {
    setSelectedNotifications(prev => 
      prev.includes(notificationId) 
        ? prev.filter(id => id !== notificationId)
        : [...prev, notificationId]
    );
  };

  // Select/Deselect all
  const toggleSelectAll = () => {
    if (selectedNotifications.length === savedNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(savedNotifications.map(n => n.id));
    }
  };

  // Load notification into form
  const loadNotification = (notification) => {
    setLetterDate(notification.letterDate || format(new Date(), 'MMMM d, yyyy'));
    setCenterInfo(notification.centerInfo || { name: "", address: "", capacity: "", primaryContact: "", primaryPhone: "", primaryCell: "", secondaryContact: "", secondaryCell: "" });
    setPickupDropoff(notification.pickupDropoff || { primaryDropOff: "", primaryPickUp: "", secondaryLocation: "" });
    setExcursionLocations(notification.excursionLocations?.length > 0 
      ? notification.excursionLocations 
      : [{ id: 1, name: "", address: "" }]);
    setCcRecipients(notification.ccRecipients?.length > 0 
      ? notification.ccRecipients 
      : [{ id: 1, name: "", address: "" }]);
    setAttachmentNote(notification.attachmentNote || "");
    setSignature(notification.signature || { name: "", title: "" });
    setView("form");
  };

  // Email notification
  const handleEmailNotification = async (notification, manualEmail = null) => {
    let email = manualEmail || recipientEmail;
    
    if (!email) {
      const inputEmail = window.prompt("Enter email address to send notification:");
      if (!inputEmail || !inputEmail.includes('@')) {
        if (inputEmail) toast.error("Please enter a valid email address");
        return;
      }
      email = inputEmail;
    } else {
      if (!window.confirm(`Send notification to ${email}?`)) return;
    }
    
    setEmailing(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/transportation-notifications/email`, {
        notificationId: notification.id,
        recipientEmail: email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Notification emailed to ${email}`);
    } catch (error) {
      console.error("Error emailing notification:", error);
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setEmailing(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF, DOC, DOCX, or image file (JPG, PNG)");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('centerName', centerInfo.name);
      formDataUpload.append('expirationDate', letterDate); // Use letter date as expiration
      
      const token = localStorage.getItem("token");
      await axios.post(`${API}/annual-excursion-letter/upload`, formDataUpload, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast.success("Annual excursion letter uploaded successfully!");
      if (onComplianceChange) onComplianceChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Add excursion location
  const addExcursionLocation = () => {
    setExcursionLocations([...excursionLocations, { id: Date.now(), name: "", address: "" }]);
  };

  // Update excursion location
  const updateExcursionLocation = (id, field, value) => {
    setExcursionLocations(excursionLocations.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  // Remove excursion location
  const removeExcursionLocation = (id) => {
    if (excursionLocations.length > 1) {
      setExcursionLocations(excursionLocations.filter(l => l.id !== id));
    }
  };

  // Add CC recipient
  const addCcRecipient = () => {
    setCcRecipients([...ccRecipients, { id: Date.now(), name: "", address: "" }]);
  };

  // Update CC recipient
  const updateCcRecipient = (id, field, value) => {
    setCcRecipients(ccRecipients.map(r => r.id === id ? { ...r, [field]: value } : r));
  };

  // Remove CC recipient
  const removeCcRecipient = (id) => {
    if (ccRecipients.length > 1) {
      setCcRecipients(ccRecipients.filter(r => r.id !== id));
    }
  };

  const handlePrint = () => {
    if (!letterRef.current) { window.print(); return; }
    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.error("Allow popups to print"); return; }
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Annual Excursion Letter</title>
    <style>
      body { font-family: Georgia, 'Times New Roman', serif; padding: 40px; max-width: 800px; margin: 0 auto; font-size: 12px; color: #000; }
      @media print { body { padding: 20px; } }
    </style></head><body>${letterRef.current.innerHTML}</body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const clearForm = () => {
    setLetterDate(format(new Date(), 'MMMM d, yyyy'));
    setCenterInfo({
      name: orgName,
      address: "",
      capacity: "",
      primaryContact: "",
      primaryPhone: "",
      primaryCell: "",
      secondaryContact: "",
      secondaryCell: ""
    });
    setPickupDropoff({ primaryDropOff: "", primaryPickUp: "", secondaryLocation: "" });
    setExcursionLocations([{ id: 1, name: "", address: "" }]);
    setCcRecipients([{ id: 1, name: "", address: "" }]);
    setAttachmentNote("");
    setSignature({ name: "", title: "" });
    setRecipientEmail("");
  };

  // Helper to render blank line for print
  const BlankLine = ({ width = "150px" }) => (
    <span className="border-b border-gray-400 inline-block min-w-[${width}] mx-1">
      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
    </span>
  );

  // History View
  if (view === "history") {
    return (
      <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200" data-testid="transportation-notification-history">
        <CardHeader className="bg-gradient-to-r from-blue-100 to-cyan-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
              <History className="w-7 h-7" />
              Saved Annual Notifications
            </CardTitle>
            <Button 
              onClick={() => setView("form")} 
              variant="outline" 
              className="border-blue-300 text-blue-600"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back to Letter
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Bulk Actions */}
          <div className="flex items-center justify-between mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Button
                onClick={toggleSelectAll}
                variant="outline"
                size="sm"
                className="border-blue-300"
                data-testid="select-all-notifications"
              >
                {selectedNotifications.length === savedNotifications.length && savedNotifications.length > 0 ? (
                  <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</>
                ) : (
                  <><Square className="w-4 h-4 mr-1" /> Select All</>
                )}
              </Button>
              <span className="text-sm text-gray-600">
                {selectedNotifications.length} of {savedNotifications.length} selected
              </span>
            </div>
            {selectedNotifications.length > 0 && (
              <Button
                onClick={handleBulkDelete}
                variant="destructive"
                size="sm"
                data-testid="bulk-delete-notifications"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete Selected ({selectedNotifications.length})
              </Button>
            )}
          </div>

          {/* Notifications List */}
          {loadingNotifications ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading notifications...</p>
            </div>
          ) : savedNotifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No saved annual notifications yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {savedNotifications.map((notification) => (
                <div 
                  key={notification.id} 
                  className={`p-4 rounded-lg border-2 transition-colors ${
                    selectedNotifications.includes(notification.id) 
                      ? 'border-blue-400 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedNotifications.includes(notification.id)}
                        onCheckedChange={() => toggleNotificationSelection(notification.id)}
                        className="border-blue-400 data-[state=checked]:bg-blue-500"
                      />
                      <div>
                        <p className="font-semibold text-blue-800">{notification.centerInfo?.name || "Unnamed Center"}</p>
                        <p className="text-sm text-gray-500">
                          {notification.letterDate} • Signed by: {notification.signature?.name || "N/A"}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {notification.excursionLocations?.filter(l => l.name).length || 0} excursion location(s) • {notification.ccRecipients?.filter(r => r.name).length || 0} CC recipient(s)
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleEmailNotification(notification)}
                        disabled={emailing}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600"
                        title="Email notification"
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => loadNotification(notification)}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-600"
                      >
                        View
                      </Button>
                      <Button
                        onClick={() => handleDeleteNotification(notification.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:bg-red-50"
                        data-testid={`delete-notification-${notification.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Form View - Letter Format
  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-blue-200" data-testid="transportation-notification-tab">
      <CardHeader className="print:hidden bg-gradient-to-r from-blue-100 to-cyan-100">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl text-blue-800 flex items-center gap-3">
              <FileText className="w-7 h-7" />
              Annual Excursion Letter
            </CardTitle>
            <p className="text-blue-600 text-sm mt-1">Formal letter to local authorities - fully customizable</p>
          </div>
          <div className="flex gap-2">
            {savedNotifications.length > 0 && (
              <Button 
                onClick={handlePrefill}
                variant="outline" 
                className="border-green-300 text-green-600 hover:bg-green-50"
                data-testid="prefill-notification"
              >
                <RefreshCw className="w-4 h-4 mr-1" /> Pre-fill from Last
              </Button>
            )}
            <Button 
              onClick={() => setView("history")} 
              variant="outline" 
              className="border-blue-300 text-blue-600"
              data-testid="view-notification-history"
            >
              <History className="w-4 h-4 mr-1" /> View Saved ({savedNotifications.length})
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6">
        {/* Toggle Form Inputs */}
        <div className="mb-4 print:hidden">
          <Button 
            onClick={() => setShowForm(!showForm)}
            variant="outline"
            size="sm"
            className="border-gray-300"
          >
            {showForm ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showForm ? "Hide Form (Show Letter Only)" : "Show Form Inputs"}
          </Button>
        </div>

        {/* Preloaded Template Notice — informational only */}
        <div className="mb-4 print:hidden bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="preloaded-template-notice">
          <FileText className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800">
            A preloaded Annual Excursion Letter template is available below for your convenience. You may use the provided template or upload your own completed document.
          </p>
        </div>

        {/* Paper Copy on File — always visible upload/drag-drop */}
        <div className="mb-4 print:hidden bg-white border-2 border-green-200 rounded-xl p-4">
          <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
            <Upload className="w-4 h-4" />
            Paper Copy on File — Upload generates compliance
          </h3>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors cursor-pointer ${
              dragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-green-50/40'
            }`}
            onClick={() => document.getElementById('excursion-file-input').click()}
          >
            <Upload className={`w-7 h-7 mx-auto mb-1.5 ${dragOver ? 'text-green-600' : 'text-gray-400'}`} />
            <p className={`text-sm ${dragOver ? 'text-green-600' : 'text-gray-500'}`}>
              {uploading ? 'Uploading...' : dragOver ? 'Drop file here' : 'Click to upload or drag & drop your excursion letter'}
            </p>
            <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
          </div>
          <input
            id="excursion-file-input"
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(e) => handleFileUpload(e.target.files?.[0])}
            className="hidden"
          />
        </div>

        {/* Form Inputs - Collapsible */}
        {showForm && (
          <div className="space-y-6 mb-8 print:hidden p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="font-bold text-gray-700 border-b pb-2">Letter Details</h3>
            
            {/* Date and Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Letter Date</Label>
                <Input
                  value={letterDate}
                  onChange={(e) => setLetterDate(e.target.value)}
                  placeholder="January 1, 2025"
                  className="border-gray-300 mt-1"
                  data-testid="notification-date"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Recipient Email (for sending)</Label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="official@city.gov"
                  className="border-gray-300 mt-1"
                  data-testid="recipient-email"
                />
              </div>
            </div>

            {/* Center Information */}
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4" />
                Center Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-600 text-sm">Center/Organization Name *</Label>
                  <Input
                    value={centerInfo.name}
                    onChange={(e) => setCenterInfo({ ...centerInfo, name: e.target.value })}
                    placeholder="Your Childcare Center Name"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Full Address *</Label>
                  <Input
                    value={centerInfo.address}
                    onChange={(e) => setCenterInfo({ ...centerInfo, address: e.target.value })}
                    placeholder="123 Main St, City, State ZIP"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Capacity</Label>
                  <Input
                    value={centerInfo.capacity}
                    onChange={(e) => setCenterInfo({ ...centerInfo, capacity: e.target.value })}
                    placeholder="e.g., 152 children"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Primary Contact Name</Label>
                  <Input
                    value={centerInfo.primaryContact}
                    onChange={(e) => setCenterInfo({ ...centerInfo, primaryContact: e.target.value })}
                    placeholder="Contact name"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Phone Number</Label>
                  <Input
                    value={centerInfo.primaryPhone}
                    onChange={(e) => setCenterInfo({ ...centerInfo, primaryPhone: e.target.value })}
                    placeholder="(XXX) XXX-XXXX"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Cell Phone</Label>
                  <Input
                    value={centerInfo.primaryCell}
                    onChange={(e) => setCenterInfo({ ...centerInfo, primaryCell: e.target.value })}
                    placeholder="(XXX) XXX-XXXX"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Secondary Contact Name</Label>
                  <Input
                    value={centerInfo.secondaryContact}
                    onChange={(e) => setCenterInfo({ ...centerInfo, secondaryContact: e.target.value })}
                    placeholder="Secondary contact"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Secondary Cell Phone</Label>
                  <Input
                    value={centerInfo.secondaryCell}
                    onChange={(e) => setCenterInfo({ ...centerInfo, secondaryCell: e.target.value })}
                    placeholder="(XXX) XXX-XXXX"
                    className="border-gray-300 mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Designated Pick-Up and Drop-Off Locations */}
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <h4 className="font-semibold text-gray-700 flex items-center gap-2 mb-3">
                <Truck className="w-4 h-4" />
                Designated Pick-Up and Drop-Off Locations
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-600 text-sm">Primary Drop-Off Location</Label>
                  <Input
                    value={pickupDropoff.primaryDropOff}
                    onChange={(e) => setPickupDropoff({ ...pickupDropoff, primaryDropOff: e.target.value })}
                    placeholder="Enter primary drop-off location"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Primary Pick-Up Location</Label>
                  <Input
                    value={pickupDropoff.primaryPickUp}
                    onChange={(e) => setPickupDropoff({ ...pickupDropoff, primaryPickUp: e.target.value })}
                    placeholder="Enter primary pick-up location"
                    className="border-gray-300 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-600 text-sm">Secondary/Overflow Location (if applicable)</Label>
                  <Input
                    value={pickupDropoff.secondaryLocation}
                    onChange={(e) => setPickupDropoff({ ...pickupDropoff, secondaryLocation: e.target.value })}
                    placeholder="Enter secondary/overflow location"
                    className="border-gray-300 mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Excursion Locations */}
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Excursion Walk Locations
                </h4>
                <Button
                  onClick={addExcursionLocation}
                  size="sm"
                  variant="outline"
                  className="border-gray-300 text-gray-600"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {excursionLocations.map((location, index) => (
                  <div key={location.id} className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                    <Input
                      value={location.name}
                      onChange={(e) => updateExcursionLocation(location.id, 'name', e.target.value)}
                      placeholder="Location name"
                      className="border-gray-300 flex-1"
                    />
                    <Input
                      value={location.address}
                      onChange={(e) => updateExcursionLocation(location.id, 'address', e.target.value)}
                      placeholder="Address"
                      className="border-gray-300 flex-1"
                    />
                    {excursionLocations.length > 1 && (
                      <Button
                        onClick={() => removeExcursionLocation(location.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-600 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* CC Recipients */}
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-700">CC Recipients (Local Authorities)</h4>
                <Button
                  onClick={addCcRecipient}
                  size="sm"
                  variant="outline"
                  className="border-gray-300 text-gray-600"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {ccRecipients.map((recipient) => (
                  <div key={recipient.id} className="flex items-center gap-2">
                    <Input
                      value={recipient.name}
                      onChange={(e) => updateCcRecipient(recipient.id, 'name', e.target.value)}
                      placeholder="Department/Office name"
                      className="border-gray-300 flex-1"
                    />
                    <Input
                      value={recipient.address}
                      onChange={(e) => updateCcRecipient(recipient.id, 'address', e.target.value)}
                      placeholder="Address (optional)"
                      className="border-gray-300 flex-1"
                    />
                    {ccRecipients.length > 1 && (
                      <Button
                        onClick={() => removeCcRecipient(recipient.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-600 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Attachment Note */}
            <div>
              <Label className="text-gray-700 font-medium">Additional Notes / Attachments</Label>
              <Textarea
                value={attachmentNote}
                onChange={(e) => setAttachmentNote(e.target.value)}
                placeholder="e.g., Also attached please find a copy of our current Emergency Plans and Procedures."
                className="border-gray-300 mt-1"
                rows={2}
              />
            </div>

            {/* Signature */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-medium">Signature Name</Label>
                <Input
                  value={signature.name}
                  onChange={(e) => setSignature({ ...signature, name: e.target.value })}
                  placeholder="Your full name"
                  className="border-gray-300 mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Title</Label>
                <Input
                  value={signature.title}
                  onChange={(e) => setSignature({ ...signature, title: e.target.value })}
                  placeholder="Director / Owner"
                  className="border-gray-300 mt-1"
                />
              </div>
            </div>
          </div>
        )}

        {/* FORMAL LETTER PREVIEW - This is the printable letter */}
        <div ref={letterRef} className="border-2 border-gray-300 rounded-lg bg-white print:border-0 print:rounded-none">
          <div className="p-8 md:p-12 print:p-8 max-w-3xl mx-auto font-serif" style={{ fontFamily: 'Georgia, Times New Roman, serif' }}>
            
            {/* Letterhead - Center Name */}
            <div className="text-center mb-10 pb-4 border-b-2 border-gray-300">
              <h1 className="text-2xl font-bold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                {centerInfo.name || <span className="text-gray-400 border-b-2 border-gray-400 pb-1">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>}
              </h1>
            </div>

            {/* Date */}
            <div className="mb-8">
              <p className="text-gray-800">{letterDate || <span className="border-b border-gray-400">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>}</p>
            </div>

            {/* Salutation */}
            <div className="mb-8">
              <p className="text-gray-800">To Whom it May Concern</p>
            </div>

            {/* Body Paragraph 1 - Center Info */}
            <div className="mb-6 text-gray-800 leading-relaxed text-justify">
              <p>
                {centerInfo.name || <span className="border-b border-gray-400">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>} located at {centerInfo.address || <span className="border-b border-gray-400">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>} operates a childcare center
                {centerInfo.capacity && <> with a capacity of {centerInfo.capacity}</>}.
                {centerInfo.primaryContact && (
                  <> Primary contact for this location is {centerInfo.primaryContact}
                    {centerInfo.primaryPhone && <> Phone Number {centerInfo.primaryPhone}</>}
                    {centerInfo.primaryCell && <> Cell Phone Number {centerInfo.primaryCell}</>}
                    {centerInfo.secondaryContact && <> and {centerInfo.secondaryContact}</>}
                    {centerInfo.secondaryCell && <> Cell Phone {centerInfo.secondaryCell}</>}.
                  </>
                )}
              </p>
            </div>

            {/* Body Paragraph 2 - Excursion Walks */}
            {excursionLocations.some(l => l.name) && (
              <div className="mb-6 text-gray-800 leading-relaxed">
                <p className="mb-2">
                  This notice is also to inform all local authorities of our excursion walks to:
                </p>
                <ul className="list-none ml-4">
                  {excursionLocations.filter(l => l.name).map((location) => (
                    <li key={location.id} className="mb-1">
                      • {location.name}{location.address && ` - ${location.address}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Designated Pick-Up and Drop-Off Locations */}
            <div className="mb-6 text-gray-800 leading-relaxed">
              <p className="font-semibold mb-3">Designated Pick-Up and Drop-Off Locations</p>
              <div className="ml-4 space-y-2">
                <p>
                  <span className="font-medium">Primary Drop-Off Location:</span>{" "}
                  {pickupDropoff.primaryDropOff || <span className="border-b border-gray-400 inline-block min-w-[250px]">&nbsp;</span>}
                </p>
                <p>
                  <span className="font-medium">Primary Pick-Up Location:</span>{" "}
                  {pickupDropoff.primaryPickUp || <span className="border-b border-gray-400 inline-block min-w-[250px]">&nbsp;</span>}
                </p>
                <p>
                  <span className="font-medium">Secondary/Overflow Location (if applicable):</span>{" "}
                  {pickupDropoff.secondaryLocation || <span className="border-b border-gray-400 inline-block min-w-[200px]">&nbsp;</span>}
                </p>
              </div>
            </div>

            {/* Body Paragraph 3 - Emergency Contact */}
            <div className="mb-8 text-gray-800 leading-relaxed text-justify">
              <p>
                In case of emergencies or alerts please contact us on the above numbers.
                {attachmentNote && <> {attachmentNote}</>}
              </p>
            </div>

            {/* Closing */}
            <div className="mb-4">
              <p className="text-gray-800">Thank You</p>
            </div>

            {/* Signature */}
            <div className="mt-12 mb-8">
              <div className="border-b border-gray-400 w-48 mb-1"></div>
              <p className="font-medium text-gray-800">{signature.name || <span className="text-gray-400">_____________________</span>}</p>
              {signature.title && <p className="text-gray-600">{signature.title}</p>}
            </div>

            {/* CC Section */}
            {ccRecipients.some(r => r.name) && (
              <div className="mt-10 pt-4 border-t border-gray-300">
                <p className="font-medium text-gray-700 mb-2">Cc:</p>
                <ul className="text-gray-600 ml-4">
                  {ccRecipients.filter(r => r.name).map((recipient) => (
                    <li key={recipient.id}>
                      {recipient.name}{recipient.address && ` - ${recipient.address}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-between print:hidden">
          <Button onClick={clearForm} variant="outline" className="border-gray-300 text-gray-600">
            Clear Form
          </Button>
          <div className="flex gap-2">
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="save-notification"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Letter"}
            </Button>
            <Button 
              onClick={() => exportTransportationPDF({
                letterDate,
                centerInfo,
                pickupDropoff,
                excursionLocations,
                ccRecipients,
                attachmentNote,
                signature
              })} 
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="export-pdf"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print Letter
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransportationNotificationTab;
